import { nextTick } from 'vue'
import { isString } from '@vue/shared'
import { isClient } from '@vueuse/core'
import { addClass, getStyle, removeClass } from '@sgui-plus/utils/dom'
import { createLoadingComponent } from './createLoading'
import type { LoadingInstance } from './createLoading'
import type { LoadingOptionsResolved, LoadingOptions } from './types'
import type { CSSProperties } from 'vue'
import { isArray } from 'lodash'
import { getLockTargetEl } from './getLockTargetEl'

let fullscreenInstance: LoadingInstance | undefined = undefined

export const LoadingService = function (
  options: LoadingOptions = {}
): LoadingInstance {
  if (!isClient) return undefined as any

  const resolved = resolveOptions(options)

  if (resolved.fullscreen && fullscreenInstance) {
    return fullscreenInstance
  }

  const instance = createLoadingComponent({
    ...resolved,
    closed: () => {
      resolved.closed?.()
      if (resolved.fullscreen) fullscreenInstance = undefined
    },
  })

  addStyle(resolved, resolved.parentEl, instance)
  addClassList(resolved, resolved.parentEl, instance, resolved.lockTargetEl)

  resolved.parentEl.vLoadingAddClassList = () =>
    addClassList(resolved, resolved.parentEl, instance, resolved.lockTargetEl)

  /**
   * add loading-number to parent.
   * because if a fullscreen loading is triggered when somewhere
   * a v-loading.body was triggered before and it's parent is
   * document.body which with a margin , the fullscreen loading's
   * destroySelf function will remove 'sg-loading-parent--relative',
   * and then the position of v-loading.body will be error.
   */
  let loadingNumber: string | null =
    resolved.parentEl.getAttribute('loading-number')
  if (!loadingNumber) {
    loadingNumber = '1'
  } else {
    loadingNumber = `${Number.parseInt(loadingNumber) + 1}`
  }
  resolved.parentEl.setAttribute('loading-number', loadingNumber)

  resolved.parentEl.appendChild(instance.$el)

  // after instance render, then modify visible to trigger transition
  nextTick(() => (instance.visible.value = resolved.visible))

  if (resolved.fullscreen) {
    fullscreenInstance = instance
  }
  return instance
}

export const LoadingServiceMap = {
  show: LoadingService,
  hide: () => {
    fullscreenInstance && fullscreenInstance.hide()
  }
}


const resolveOptions = (options: LoadingOptions): LoadingOptionsResolved => {
  let target: HTMLElement
  if (isString(options.parent) || isArray(options.parent)) {
    let temp = isString(options.parent)
      ? document.querySelector<HTMLElement>(`#${options.parent}`)
      : document.querySelector<HTMLElement>(options.parent.map((item, index) => {
        if (!index) return `#${item}`
        return `.${item}`
      }).join(' '))
    target = temp ?? document.body
  } else {
    target = options.parent || document.body
  }
  return {
    id: options.id ?? '',
    parentEl: target === document.body || options.transfer ? document.body : target,
    backgroundColor: options.backgroundColor || '',
    color: options.color || '',
    size: options.size || 'large',
    fix: options.fix ?? true,
    spinner: options.spinner || false,
    text: options.text || '',
    fullscreen: target === document.body && (options.fullscreen ?? true),
    lock: options.lock ?? false,
    lockTarget: options.lockTarget ?? 'body',
    lockTargetEl: getLockTargetEl(options.lockTarget ?? ''),
    customClass: options.customClass || '',
    visible: options.visible ?? true,
    render: options.render ?? undefined,
    parent: target,
  }
}

const addStyle = async (
  options: LoadingOptionsResolved,
  parent: HTMLElement,
  instance: LoadingInstance
) => {
  const { nextZIndex } = (instance.vm.component as any).zIndex || instance.vm.component?.exposed?.zIndex

  const maskStyle: CSSProperties = {}
  if (options.fullscreen) {
    instance.originalPosition.value = getStyle(document.body, 'position')
    instance.originalOverflow.value = getStyle(document.body, 'overflow')
    maskStyle.zIndex = nextZIndex()
  } else if (options.parentEl === document.body) {
    instance.originalPosition.value = getStyle(document.body, 'position')
    /**
     * await dom render when visible is true in init,
     * because some component's height maybe 0.
     * e.g. sg-table.
     */
    await nextTick()
    for (const property of ['top', 'left']) {
      const scroll = property === 'top' ? 'scrollTop' : 'scrollLeft'
      maskStyle[property] = `${
        (options.parent as HTMLElement).getBoundingClientRect()[property] +
        document.body[scroll] +
        document.documentElement[scroll] -
        Number.parseInt(getStyle(document.body, `margin-${property}`), 10)
      }px`
    }
    for (const property of ['height', 'width']) {
      maskStyle[property] = `${
        (options.parent as HTMLElement).getBoundingClientRect()[property]
      }px`
    }
  } else {
    instance.originalPosition.value = getStyle(parent, 'position')
  }
  for (const [key, value] of Object.entries(maskStyle)) {
    instance.$el.style[key] = value
  }
}

const addClassList = (
  options: LoadingOptionsResolved,
  parent: HTMLElement,
  instance: LoadingInstance,
  lockTargetEl: HTMLElement | null
) => {
  if (
    instance.originalPosition.value !== 'absolute' &&
    instance.originalPosition.value !== 'fixed'
  ) {
    addClass(parent, 'sg-loading-parent--relative')
  } else {
    removeClass(parent, 'sg-loading-parent--relative')
  }
  if (options.fullscreen && options.lock) {
    lockTargetEl && addClass(lockTargetEl, 'sg-loading-parent--hidden')
  } else {
    lockTargetEl && removeClass(lockTargetEl, 'sg-loading-parent--hidden')
  }
}
